import { useEffect, useState } from "react";
import api from "@/lib/api";
import { SectionCard } from "@/components/shared/PageChrome";
import { StatusBadge } from "@/components/shared/StatusBadge";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";

const fmt = (d) => (d ? new Date(d).toLocaleString("en-GB", { day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit" }) : "—");
const CHANNEL = { CLIENT_WORKSPACE: "Client workspace", DOCUMENT: "Document", EMAIL: "E-mail" };
const TONE = { EMAIL_SENT: "text-jigsaw-success", EMAIL_FAILED: "text-jigsaw-danger", EMAIL_QUEUED: "text-jigsaw-warning" };

// Staff-only, read-only. Presentation, document, queue and dispatch-attempt events are kept distinct; nothing here is called "sent" without a recorded attempt.
export function OptionsLogPanel({ enquiryId }) {
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  useEffect(() => { api.get(`/enquiries/${enquiryId}/options-log`).then((r) => { setData(r.data); setError(null); }).catch((e) => setError(e.response?.data?.detail || "Could not load the options log")); }, [enquiryId]);
  return (
    <SectionCard title="Options presented and sent" subtitle="Every presentation, document and e-mail event for this enquiry's options — ‘sent’ appears only where the mail provider accepted a dispatch attempt" testId="options-log">
      {error && <p className="text-sm text-jigsaw-danger" data-testid="options-log-error">{error}</p>}
      {data && data.items.length === 0 && <p className="text-sm text-jigsaw-muted" data-testid="options-log-empty">No options have been presented, generated or e-mailed yet.</p>}
      {data && data.items.length > 0 && (
        <div className="overflow-x-auto">
          <div className="mb-2 flex flex-wrap gap-2 text-[12px] text-jigsaw-muted" data-testid="options-log-counts">
            {Object.entries(data.counts).map(([k, n]) => <span key={k} className="rounded-full border border-jigsaw-border px-2 py-0.5">{data.kinds[k]}: {n}</span>)}
          </div>
          <Table data-testid="options-log-table">
            <TableHeader><TableRow><TableHead>When</TableHead><TableHead>Event</TableHead><TableHead>Version</TableHead><TableHead>Options included</TableHead><TableHead>By</TableHead><TableHead>Channel</TableHead><TableHead>Outcome</TableHead></TableRow></TableHeader>
            <TableBody>
              {data.items.map((r) => (
                <TableRow key={r.id} data-testid={`options-log-row-${r.id}`}>
                  <TableCell className="whitespace-nowrap text-[13px]">{fmt(r.at)}</TableCell>
                  <TableCell className={`text-[13px] font-medium ${TONE[r.kind] || "text-jigsaw-navy"}`}>{r.label}</TableCell>
                  <TableCell className="text-[13px]">{r.version ? `v${r.version}` : "—"}</TableCell>
                  <TableCell className="max-w-[18rem] text-[13px]">{r.options.join("; ")}</TableCell>
                  <TableCell className="text-[13px]">{r.actor_name}</TableCell>
                  <TableCell className="text-[13px]">{CHANNEL[r.channel] || r.channel}</TableCell>
                  <TableCell><span className="flex flex-col gap-0.5">{r.outcome ? <StatusBadge status={r.outcome} /> : <span className="text-[12px] text-jigsaw-muted">—</span>}{r.detail && <span className="text-[11px] text-jigsaw-muted">{r.detail}</span>}</span></TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      )}
    </SectionCard>
  );
}
